import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'

import {
  POOL_OTHER_GROUP_ID,
  POOL_SIGNED_GROUP_ID,
} from '../lib/boardInvariants'
import {
  copyPlayerFilters,
  emptyPlayerFilters,
  PlayerFilterMode,
  playerFilterCount,
  playerFiltersAreActive,
  playerMatchesFilters,
} from '../lib/playerFilters'
import type {
  PlayerFilterMode as PlayerFilterModeType,
  PlayerFilters,
} from '../lib/playerFilters'
import { playerDisplayName } from '../lib/playerIdentity'
import {
  firstPlayerBuildChoice,
  raidBuildIsSelected,
} from '../lib/playerWeaponBuilds'
import type { PlayerBuildChoice } from '../lib/playerWeaponBuilds'
import { useGvgPlannerStore } from '../stores/gvgPlannerStore'
import type { Player } from '../types'

export interface UsePlayerFiltersOptions {
  readonly announce: (message: string) => void
}

interface PendingTagAssignment {
  readonly tagId: string
  readonly selected: boolean
}

export interface PlayerFiltersUi {
  readonly drawerOpen: Ref<boolean>
  readonly boardFilters: Ref<PlayerFilters>
  readonly boardMode: Ref<PlayerFilterModeType>
  readonly selectorGroupId: Ref<string | null>
  readonly selectorTagId: Ref<string | null>
  readonly pendingTargets: Ref<ReadonlyMap<string, string>>
  readonly pendingTagPlayerIds: ComputedRef<ReadonlySet<string>>
  readonly activeFilterCount: ComputedRef<number>
  readonly boardFiltersActive: ComputedRef<boolean>
  readonly matchingPlayerIds: ComputedRef<ReadonlySet<string>>
  readonly drawerFilters: ComputedRef<PlayerFilters>
  readonly openBoardFilters: () => void
  readonly openGroupSelector: (groupId: string) => void
  readonly openTagSelector: (tagId: string) => void
  readonly updateDrawerFilters: (filters: PlayerFilters) => void
  readonly toggleGroupPlayer: (
    playerId: string,
    selected: boolean,
    buildChoice?: PlayerBuildChoice | null,
  ) => Promise<void>
  readonly toggleTagPlayer: (playerId: string, selected: boolean) => Promise<void>
}

export function usePlayerFilters(options: UsePlayerFiltersOptions): PlayerFiltersUi {
  const store = useGvgPlannerStore()
  const drawerOpen = ref(false)
  const boardFilters = ref<PlayerFilters>(emptyPlayerFilters())
  const groupFilters = ref<PlayerFilters>(emptyPlayerFilters())
  const tagFilters = ref<PlayerFilters>(emptyPlayerFilters())
  const boardMode = ref<PlayerFilterModeType>(PlayerFilterMode.Filter)
  const selectorGroupId = ref<string | null>(null)
  const selectorTagId = ref<string | null>(null)
  const pendingTargets = ref<ReadonlyMap<string, string>>(new Map())
  const pendingTagAssignments = ref<ReadonlyMap<string, PendingTagAssignment>>(new Map())
  const movingPlayerIds = ref<ReadonlySet<string>>(new Set())

  const activeFilterCount = computed<number>(() => playerFilterCount(boardFilters.value))
  const boardFiltersActive = computed<boolean>(() => playerFiltersAreActive(boardFilters.value))
  const matchingPlayerIds = computed<ReadonlySet<string>>(() =>
    new Set(
      store.currentPlayers
        .filter((player) => playerMatchesFilters(player, boardFilters.value))
        .map((player) => player.id),
    ),
  )
  const pendingTagPlayerIds = computed<ReadonlySet<string>>(
    () => new Set(pendingTagAssignments.value.keys()),
  )
  const drawerFilters = computed<PlayerFilters>(() =>
    selectorGroupId.value !== null
      ? groupFilters.value
      : selectorTagId.value !== null
        ? tagFilters.value
        : boardFilters.value,
  )

  watch(
    () => store.currentPlayers,
    (players) => {
      if (pendingTargets.value.size > 0) {
        const next = new Map(pendingTargets.value)
        for (const [playerId, targetGroupId] of pendingTargets.value) {
          if (movingPlayerIds.value.has(playerId)) continue
          const player = players.find((candidate) => candidate.id === playerId)
          if (player?.group === targetGroupId) next.delete(playerId)
        }
        if (next.size !== pendingTargets.value.size) pendingTargets.value = next
      }

      if (pendingTagAssignments.value.size > 0) {
        const next = new Map(pendingTagAssignments.value)
        for (const [playerId, assignment] of pendingTagAssignments.value) {
          const player = players.find((candidate) => candidate.id === playerId)
          const hasTag = player?.tags.some((tag) => tag.id === assignment.tagId) ?? false
          if (hasTag === assignment.selected) next.delete(playerId)
        }
        if (next.size !== pendingTagAssignments.value.size) pendingTagAssignments.value = next
      }
    },
  )

  function openBoardFilters(): void {
    selectorGroupId.value = null
    selectorTagId.value = null
    drawerOpen.value = true
  }

  function openGroupSelector(groupId: string): void {
    groupFilters.value = copyPlayerFilters(boardFilters.value)
    selectorGroupId.value = groupId
    selectorTagId.value = null
    drawerOpen.value = true
  }

  function openTagSelector(tagId: string): void {
    const nextFilters = copyPlayerFilters(boardFilters.value)
    tagFilters.value = {
      ...nextFilters,
      tagIds: nextFilters.tagIds.filter((candidate) => candidate !== tagId),
    }
    selectorGroupId.value = null
    selectorTagId.value = tagId
    drawerOpen.value = true
  }

  function updateDrawerFilters(filters: PlayerFilters): void {
    if (selectorGroupId.value !== null) {
      groupFilters.value = filters
      return
    }
    if (selectorTagId.value !== null) {
      tagFilters.value = filters
      return
    }
    boardFilters.value = filters
  }

  function updatePendingTarget(playerId: string, targetGroupId: string | null): void {
    const next = new Map(pendingTargets.value)
    if (targetGroupId === null) next.delete(playerId)
    else next.set(playerId, targetGroupId)
    pendingTargets.value = next
  }

  function updatePendingTagAssignment(playerId: string, assignment: PendingTagAssignment | null): void {
    const next = new Map(pendingTagAssignments.value)
    if (assignment === null) next.delete(playerId)
    else next.set(playerId, assignment)
    pendingTagAssignments.value = next
  }

  function setPlayerMoving(playerId: string, moving: boolean): void {
    const next = new Set(movingPlayerIds.value)
    if (moving) next.add(playerId)
    else next.delete(playerId)
    movingPlayerIds.value = next
  }

  function pendingInsertIndex(targetGroupId: string): number {
    const group = store.currentGroups.find((candidate) => candidate.id === targetGroupId)
    let pendingCount = 0
    for (const pendingTarget of pendingTargets.value.values()) {
      if (pendingTarget === targetGroupId) pendingCount += 1
    }
    return (group?.playerIds.length ?? 0) + pendingCount
  }

  function displayPlayerName(player: Player): string {
    return playerDisplayName(player, store.playerNameMode)
  }

  async function toggleGroupPlayer(
    playerId: string,
    selected: boolean,
    buildChoice: PlayerBuildChoice | null = null,
  ): Promise<void> {
    if (store.activeRaidEditLocked) return
    const player = store.currentPlayers.find((candidate) => candidate.id === playerId)
    const selectedGroupId = selectorGroupId.value
    if (player === undefined || selectedGroupId === null || pendingTargets.value.has(playerId)) return

    const targetGroupId = selected
      ? selectedGroupId
      : player.hasSignedIn
        ? POOL_SIGNED_GROUP_ID
        : POOL_OTHER_GROUP_ID
    const targetGroup = store.currentGroups.find((candidate) => candidate.id === targetGroupId)
    if (targetGroup === undefined) {
      options.announce(`Could not find the ${selected ? 'selected' : player.hasSignedIn ? 'Signed' : 'Other'} group`)
      return
    }
    if (player.group === targetGroupId) return

    const insertIndex = pendingInsertIndex(targetGroupId)
    updatePendingTarget(playerId, targetGroupId)
    setPlayerMoving(playerId, true)
    const fallbackChoice = selected
      && store.activeRaidId !== null
      && !raidBuildIsSelected(
        store.raidPlayers.find((candidate) => candidate.id === playerId)?.activeBuildIndex,
      )
      ? firstPlayerBuildChoice(player, groupFilters.value.weaponSlugs)
      : null
    const raidBuildChoice = selected && store.activeRaidId !== null
      ? buildChoice ?? fallbackChoice
      : null
    if (raidBuildChoice !== null) {
      const buildSelected = await store.setRaidPlayerBuildPair(
        playerId,
        raidBuildChoice.weaponSlugs,
      )
      if (!buildSelected) {
        setPlayerMoving(playerId, false)
        updatePendingTarget(playerId, null)
        options.announce(`Could not select a raid build for ${displayPlayerName(player)}`)
        return
      }
    }
    const moved = await store.movePlayerToGroup(playerId, targetGroupId, insertIndex)
    setPlayerMoving(playerId, false)
    if (!moved) {
      updatePendingTarget(playerId, null)
      options.announce(`Could not move ${displayPlayerName(player)}`)
      return
    }
    const movedPlayer = store.currentPlayers.find((candidate) => candidate.id === playerId)
    if (movedPlayer?.group === targetGroupId) updatePendingTarget(playerId, null)
    options.announce(
      selected
        ? `Moved ${displayPlayerName(player)} to ${targetGroup.label || targetGroup.id}`
        : `Returned ${displayPlayerName(player)} to ${player.hasSignedIn ? 'Signed' : 'Other'}`,
    )
  }

  async function toggleTagPlayer(playerId: string, selected: boolean): Promise<void> {
    if (store.activeRaidEditLocked) return
    const tagId = selectorTagId.value
    const player = store.currentPlayers.find((candidate) => candidate.id === playerId)
    const tag = tagId === null ? undefined : store.playerTags.find((candidate) => candidate.id === tagId)
    if (!store.isOwner && !store.isOfficer) return
    if (player === undefined || tag === undefined || pendingTagAssignments.value.has(playerId)) return
    if (player.tags.some((candidate) => candidate.id === tag.id) === selected) return

    updatePendingTagAssignment(playerId, { tagId: tag.id, selected })
    const changed = selected
      ? await store.assignTag(playerId, tag.id)
      : await store.unassignTag(playerId, tag.id)
    if (!changed) {
      updatePendingTagAssignment(playerId, null)
      options.announce(`Could not ${selected ? 'assign' : 'remove'} ${tag.name} for ${displayPlayerName(player)}`)
      return
    }
    options.announce(`${selected ? 'Assigned' : 'Removed'} ${tag.name} ${selected ? 'to' : 'from'} ${displayPlayerName(player)}`)
  }

  return {
    drawerOpen,
    boardFilters,
    boardMode,
    selectorGroupId,
    selectorTagId,
    pendingTargets,
    pendingTagPlayerIds,
    activeFilterCount,
    boardFiltersActive,
    matchingPlayerIds,
    drawerFilters,
    openBoardFilters,
    openGroupSelector,
    openTagSelector,
    updateDrawerFilters,
    toggleGroupPlayer,
    toggleTagPlayer,
  }
}
